import React, { useEffect, useRef, useState } from 'react';
import { Divider, IconButton, Stack } from '@mui/material';
import { Form, FormRef } from 'common/form';
import {
  DeleteOutline,
  Edit,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import { useModalV2 } from 'common/modal/hooks/useModalV2';

export type BaseQuestionProps<T extends Record<string, unknown>> = {
  id: string;
  isFirst: boolean;
  isLast: boolean;
  editByDefault?: boolean;
  setQuestionValue: (id: string, value: string) => void;
  onDelete: () => void;
  moveQuestion: ({
    id,
    direction
  }: {
    id: string;
    direction: 'up' | 'down';
  }) => void;
  schema: any;
  fields: any;
  formDefaultValues: T;
  renderViewMode: (editing: boolean) => React.ReactNode;
  onWatch?: (formRef: FormRef<T>, id: string) => () => void;
  onCancel: (initialValues: T) => boolean | void;
};

export function BaseQuestion<T extends Record<string, unknown>>(
  props: BaseQuestionProps<T>
) {
  const {
    id,
    isFirst,
    isLast,
    editByDefault,
    onDelete,
    moveQuestion,
    schema,
    fields,
    formDefaultValues,
    renderViewMode,
    onWatch,
    onCancel
  } = props;

  const formRef = useRef<FormRef<T>>(null);
  const [editing, setEditing] = useState(editByDefault ?? false);
  const [initialValues, setInitialValues] = useState<T>(formDefaultValues);

  // Create a copy of fields with autoFocus set on the first visible field
  const fieldsWithAutoFocus = React.useMemo(() => {
    if (!editing) return fields;

    const fieldsWithAutoFocus = { ...fields };
    // Find the first visible field that's a text or textarea
    const firstFieldKey = Object.keys(fields).find(
      (key) =>
        !fields[key].hidden &&
        (fields[key].type === 'text' ||
          fields[key].type === 'textarea' ||
          fields[key].type === 'chipInput')
    );

    if (firstFieldKey) {
      fieldsWithAutoFocus[firstFieldKey] = {
        ...fields[firstFieldKey],
        autoFocus: true
      };
    }

    return fieldsWithAutoFocus;
  }, [editing, fields]);

  useEffect(() => {
    if (!formRef?.current || !editing || !onWatch) {
      return;
    }

    const subscription = onWatch(formRef.current, id);

    return () => subscription();
  }, [formRef, editing, id, onWatch]);

  const handleSave = (formData: T) => {
    setInitialValues(formData);
    setEditing(false);
  };

  const handleCancel = () => {
    // Only exit edit mode if onCancel returns true or undefined
    const canCancel = onCancel(initialValues);
    if (canCancel !== false) {
      setEditing(false);
    }
  };

  const { openModal } = useModalV2();

  const handleDeleteClick = () => {
    openModal({
      title: 'Delete question?',
      subtitle:
        'Your question will be deleted immediately. This cannot be undone.',
      primaryButtonText: 'Cancel',
      secondaryButtonText: 'Delete',
      onSecondaryAction: onDelete,
      secondaryButtonProps: { variant: 'tertiary', state: 'destructive' }
    });
  };

  return (
    <Stack gap="30px">
      <Stack direction="row" alignItems="flex-start" gap="16px">
        {renderViewMode(editing)}

        {editing && (
          <Form
            ref={formRef}
            sx={{ flex: 1, gap: '16px' }}
            defaultValues={formDefaultValues}
            schema={schema}
            fields={fieldsWithAutoFocus}
            submitButtonProps={{
              label: 'Save',
              variant: 'primary'
            }}
            cancelButtonProps={{
              variant: 'tertiary'
            }}
            onSubmit={handleSave}
            onCancel={handleCancel}
          />
        )}

        {!editing && (
          <Stack direction="row" gap="15px" mt="28px" alignItems="center">
            <IconButton
              size="small"
              onClick={() => setEditing(true)}
              color="primary"
            >
              <Edit />
            </IconButton>
            {!isFirst && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => moveQuestion({ id, direction: 'up' })}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            {!isLast && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => moveQuestion({ id, direction: 'down' })}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
            <IconButton size="small" color="error" onClick={handleDeleteClick}>
              <DeleteOutline />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Divider />
    </Stack>
  );
}
